import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { database } from '../../firebase';
import { ref, get } from 'firebase/database';
import Swal from 'sweetalert2';
import {
  CreditCardIcon, CalendarIcon, CheckCircleIcon,
  XMarkIcon, ArrowPathIcon, ChevronDownIcon,
  SparklesIcon, WalletIcon, BanknotesIcon,
  DevicePhoneMobileIcon, ArrowDownTrayIcon,
  ExclamationTriangleIcon, ClockIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const PRIMARY = '#800020';
const DARK    = '#1a1a2e';

const SWAL_Z = { customClass: { container: 'pay-swal-z' } };
if (typeof document !== 'undefined' && !document.getElementById('pay-swal-z-style')) {
  const s = document.createElement('style');
  s.id = 'pay-swal-z-style';
  s.textContent = '.pay-swal-z{z-index:99999!important}';
  document.head.appendChild(s);
}
const toast = (icon, title, text = '') =>
  Swal.fire({ icon, title, text, timer: 2500, toast: true, position: 'top-end', showConfirmButton: false, ...SWAL_Z });

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const fmtDate = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-KE', { day: 'numeric', month: 'long', year: 'numeric' });
};
const fmtDateTime = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-KE', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const METHOD_ICON = {
  visa:       <CreditCardIcon      className="h-4 w-4" />,
  mastercard: <CreditCardIcon      className="h-4 w-4" />,
  card:       <CreditCardIcon      className="h-4 w-4" />,
  paystack:   <CreditCardIcon      className="h-4 w-4" />,
  mpesa:      <DevicePhoneMobileIcon className="h-4 w-4" />,
  wallet:     <WalletIcon          className="h-4 w-4" />,
  cash:       <BanknotesIcon       className="h-4 w-4" />,
};

const METHOD_COLOR = {
  visa:       { color: '#2563eb', bg: '#eff6ff' },
  mastercard: { color: '#dc2626', bg: '#fef2f2' },
  card:       { color: '#2563eb', bg: '#eff6ff' },
  paystack:   { color: '#2563eb', bg: '#eff6ff' },
  mpesa:      { color: '#16a34a', bg: '#f0fdf4' },
  wallet:     { color: PRIMARY,   bg: '#fef2f2' },
  cash:       { color: '#d97706', bg: '#fffbeb' },
};

const STATUS_STYLE = {
  completed: { label: 'Completed', color: '#16a34a', bg: '#f0fdf4' },
  pending:   { label: 'Pending',   color: '#d97706', bg: '#fffbeb' },
  cancelled: { label: 'Cancelled', color: '#dc2626', bg: '#fef2f2' },
  failed:    { label: 'Failed',    color: '#dc2626', bg: '#fef2f2' },
};

// ─── PDF EXPORT ───────────────────────────────────────────────────────────────
const exportPaymentsPDF = (uids, walletTxns, userInfo) => {
  const dateStr = new Date().toLocaleDateString('en-KE', { year: 'numeric', month: 'long', day: 'numeric' });

  const uidRows = uids.map(uid => `
    <tr>
      <td>${uid.packageName || uid.packageId || '—'}</td>
      <td>${uid.planKey || '—'}</td>
      <td style="text-align:right">KES ${(uid.packagePrice || 0).toLocaleString()}</td>
      <td style="color:${STATUS_STYLE[uid.status]?.color || '#6b7280'};font-weight:600">${STATUS_STYLE[uid.status]?.label || uid.status}</td>
      <td>${fmtDate(uid.activationDate)}</td>
      <td>${fmtDate(uid.expiryDate)}</td>
    </tr>
  `).join('');

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>Payment History</title>
  <style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:11px;color:#1a1a2e;padding:24px}
  .title{font-size:20px;font-weight:800;color:#800020;margin-bottom:4px}.subtitle{font-size:11px;color:#6b7280;margin-bottom:20px}
  table{width:100%;border-collapse:collapse;font-size:10.5px}
  thead tr{background:#1a1a2e;color:#fff}th{padding:8px 6px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:.04em}
  tbody tr:nth-child(even){background:#f8f9fa}td{padding:6px;border-bottom:1px solid #f1f5f9}
  .footer{margin-top:16px;font-size:10px;color:#9ca3af;text-align:right}
  @media print{body{padding:10mm}}</style></head>
  <body>
  <div class="title">💳 Payment History</div>
  <div class="subtitle">Member: ${userInfo.name} · ${userInfo.email} · Generated ${dateStr}</div>
  <table><thead><tr><th>Package</th><th>Plan</th><th>Amount</th><th>Status</th><th>Activation</th><th>Expiry</th></tr></thead>
  <tbody>${uidRows}</tbody></table>
  <div class="footer">PowerCore Gym · ${dateStr}</div>
  <script>window.onload=()=>window.print()</script></body></html>`;

  const w = window.open('', '_blank');
  w.document.write(html);
  w.document.close();
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const Payments = () => {
  const { userData, currentUser } = useAuth();

  const [paymentUIDs,   setPaymentUIDs]   = useState([]);
  const [walletTxns,    setWalletTxns]    = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [activeTab,     setActiveTab]     = useState('uids');
  const [expandedUID,   setExpandedUID]   = useState(null);
  const [filterStatus,  setFilterStatus]  = useState('all');

  const pi         = userData?.personalInfo || {};
  const membership = userData?.membership   || {};
  const wallet     = userData?.wallet       || {};

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [uidSnap, walletSnap] = await Promise.all([
        get(ref(database, `users/${currentUser.uid}/paymentUIDs`)),
        get(ref(database, `users/${currentUser.uid}/wallet/transactions`)),
      ]);

      setPaymentUIDs(uidSnap.exists()
        ? Object.entries(uidSnap.val())
            .map(([k, v]) => ({ id: k, ...v }))
            .sort((a, b) => (b.createdAt || b.activationDate || 0) - (a.createdAt || a.activationDate || 0))
        : []);

      setWalletTxns(walletSnap.exists()
        ? Object.entries(walletSnap.val())
            .map(([k, v]) => ({ id: k, ...v }))
            .sort((a, b) => b.date - a.date)
        : []);
    } catch (err) {
      console.error(err);
      toast('error', 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, [currentUser.uid]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Stats ─────────────────────────────────────────────────────────────────
  const totalPaid      = paymentUIDs.filter(u => u.status === 'completed').reduce((s, u) => s + (u.packagePrice || 0), 0);
  const completedCount = paymentUIDs.filter(u => u.status === 'completed').length;
  const pendingCount   = paymentUIDs.filter(u => u.status === 'pending').length;

  // ── Filtered UIDs ─────────────────────────────────────────────────────────
  const filteredUIDs = paymentUIDs.filter(u =>
    filterStatus === 'all' || u.status === filterStatus
  );

  const TABS = [
    { key: 'uids',   label: 'Membership Payments', icon: <SparklesIcon   className="h-4 w-4" /> },
    { key: 'wallet', label: 'Wallet History',       icon: <WalletIcon     className="h-4 w-4" /> },
  ];

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg,#f8f9fa 0%,#f0f0f0 100%)' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">

        {/* ── HEADER ── */}
        <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-black text-gray-900">Payment History</h1>
              <p className="text-gray-500 text-sm mt-1">Membership payments and wallet transactions</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={fetchAll}
                className="w-9 h-9 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-gray-500 hover:text-gray-800 shadow-sm transition-all">
                <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={() => exportPaymentsPDF(paymentUIDs, walletTxns, { name: pi.name, email: pi.email })}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white shadow-lg transition-all"
                style={{ background: `linear-gradient(135deg,${PRIMARY},#b30000)` }}>
                <ArrowDownTrayIcon className="h-4 w-4" />
                Export PDF
              </button>
            </div>
          </div>
        </motion.div>

        {/* ── CURRENT MEMBERSHIP BANNER ── */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="rounded-2xl overflow-hidden mb-6 shadow-lg">
          <div className="flex items-center gap-5 p-5"
            style={{ background: `linear-gradient(135deg,${DARK},${PRIMARY})` }}>
            <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(255,255,255,0.15)' }}>
              <SparklesIcon className="h-7 w-7 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-white/60 text-xs font-medium">Current Membership</p>
              <p className="text-white font-black text-xl">
                {membership.packageName || '—'} · {membership.planKey || '—'}
              </p>
              <p className="text-white/60 text-xs mt-0.5">
                {membership.startDate ? `${fmtDate(membership.startDate)} → ${fmtDate(membership.expiryDate)}` : 'No active membership'}
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-white/60 text-xs">Status</p>
              <span className={`text-sm font-black px-3 py-1 rounded-full mt-1 inline-block capitalize ${
                membership.status === 'active' ? 'bg-green-400 text-white' : 'bg-white/20 text-white'
              }`}>
                {membership.status || 'Inactive'}
              </span>
            </div>
          </div>
        </motion.div>

        {/* ── STAT PILLS ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total Paid',       val: `KES ${totalPaid.toLocaleString()}`, color: PRIMARY,   icon: '💳' },
            { label: 'Completed Plans',  val: completedCount,                       color: '#16a34a', icon: '✅' },
            { label: 'Pending',          val: pendingCount,                         color: '#d97706', icon: '⏳' },
            { label: 'Wallet Balance',   val: `KES ${(wallet.balance || 0).toLocaleString()}`, color: '#2563eb', icon: '👛' },
          ].map((s, i) => (
            <motion.div key={i}
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.05 }}
              className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <p className="text-xl mb-1">{s.icon}</p>
              <p className="text-xs text-gray-500">{s.label}</p>
              <p className="font-black text-base mt-0.5" style={{ color: s.color }}>{s.val}</p>
            </motion.div>
          ))}
        </div>

        {/* ── TABS ── */}
        <div className="flex gap-1 bg-white rounded-xl p-1 shadow-sm border border-gray-100 mb-6 w-fit">
          {TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold transition-all"
              style={{
                background: activeTab === tab.key ? PRIMARY : 'transparent',
                color:      activeTab === tab.key ? '#fff'  : '#6b7280',
              }}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block w-8 h-8 border-4 border-red-200 border-t-red-700 rounded-full animate-spin" />
            <p className="text-gray-500 mt-3 text-sm">Loading payments…</p>
          </div>
        ) : (
          <>
            {/* ══════════════════════════════════════════════════
                MEMBERSHIP PAYMENTS TAB
            ══════════════════════════════════════════════════ */}
            {activeTab === 'uids' && (
              <div>
                {/* Filter */}
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  {['all', 'completed', 'pending', 'cancelled'].map(s => (
                    <button key={s} onClick={() => setFilterStatus(s)}
                      className="px-4 py-2 rounded-xl text-xs font-bold transition-all capitalize"
                      style={{
                        background: filterStatus === s ? PRIMARY : '#fff',
                        color:      filterStatus === s ? '#fff'  : '#6b7280',
                        border:     `2px solid ${filterStatus === s ? PRIMARY : '#e5e7eb'}`,
                      }}>
                      {s === 'all' ? 'All' : STATUS_STYLE[s]?.label || s}
                    </button>
                  ))}
                </div>

                {filteredUIDs.length === 0 ? (
                  <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 shadow-sm">
                    <CreditCardIcon className="h-12 w-12 text-gray-200 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">No payment records found</p>
                    <p className="text-gray-400 text-sm mt-1">
                      Membership payments will appear here once you subscribe to a plan
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredUIDs.map((uid, i) => {
                      const ss        = STATUS_STYLE[uid.status] || STATUS_STYLE.pending;
                      const isExpanded = expandedUID === uid.id;
                      const payments  = uid.payments ? Object.entries(uid.payments).map(([k, v]) => ({ key: k, ...v })) : [];
                      const totalIndexes = payments.reduce((s, p) => s + (p.amount || 0), 0);

                      return (
                        <motion.div key={uid.id}
                          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.04 }}
                          className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-all">

                          {/* UID Header */}
                          <button
                            onClick={() => setExpandedUID(isExpanded ? null : uid.id)}
                            className="w-full flex items-center gap-4 p-5 text-left hover:bg-gray-50 transition-colors">

                            {/* Package icon */}
                            <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                              style={{ background: `${ss.color}15` }}>
                              <SparklesIcon className="h-6 w-6" style={{ color: ss.color }} />
                            </div>

                            {/* Info */}
                            <div className="flex-1 min-w-0 text-left">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-black text-gray-900">{uid.packageName || uid.packageId || '—'}</p>
                                <span className="text-xs font-bold px-2 py-0.5 rounded-full capitalize"
                                  style={{ background: ss.bg, color: ss.color }}>
                                  {ss.label}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {uid.planKey && <span className="capitalize">{uid.planKey}</span>}
                                {uid.activationDate && <span> · Activated {fmtDate(uid.activationDate)}</span>}
                                {uid.expiryDate && <span> · Expires {fmtDate(uid.expiryDate)}</span>}
                              </p>
                              <div className="flex items-center gap-3 mt-1.5">
                                <span className="text-xs text-gray-400">
                                  {payments.length} payment{payments.length !== 1 ? 's' : ''} · KES {totalIndexes.toLocaleString()} / KES {(uid.packagePrice || 0).toLocaleString()}
                                </span>
                                {/* Progress bar */}
                                <div className="flex-1 max-w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                  <div className="h-full rounded-full transition-all"
                                    style={{
                                      width: `${Math.min(100, uid.packagePrice ? (totalIndexes / uid.packagePrice) * 100 : 0)}%`,
                                      background: ss.color,
                                    }} />
                                </div>
                              </div>
                            </div>

                            {/* Amount + expand */}
                            <div className="text-right flex-shrink-0">
                              <p className="font-black text-lg" style={{ color: PRIMARY }}>
                                KES {(uid.packagePrice || 0).toLocaleString()}
                              </p>
                              <div className="mt-1 transition-transform duration-200 flex justify-end"
                                style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                                <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                              </div>
                            </div>
                          </button>

                          {/* Expanded: payment indexes */}
                          <AnimatePresence>
                            {isExpanded && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.25 }}
                                className="overflow-hidden border-t border-gray-100">
                                <div className="p-5 pt-4" style={{ background: '#fafafa' }}>
                                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">
                                    Payment Indexes
                                  </p>

                                  {payments.length === 0 ? (
                                    <p className="text-sm text-gray-400 italic">No payment indexes recorded</p>
                                  ) : (
                                    <div className="space-y-2">
                                      {payments.sort((a, b) => (a.index ?? 0) - (b.index ?? 0)).map(pay => {
                                        const mc = METHOD_COLOR[pay.method?.toLowerCase()] || { color: '#6b7280', bg: '#f9fafb' };
                                        return (
                                          <div key={pay.key}
                                            className="flex items-center gap-3 p-3 rounded-xl bg-white border border-gray-100">
                                            {/* Index badge */}
                                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-black text-white flex-shrink-0"
                                              style={{ background: PRIMARY }}>
                                              {(pay.index ?? 0) + 1}
                                            </div>

                                            {/* Method */}
                                            <div className="flex items-center gap-2 px-2.5 py-1 rounded-lg flex-shrink-0"
                                              style={{ background: mc.bg, color: mc.color }}>
                                              {METHOD_ICON[pay.method?.toLowerCase()] || <CreditCardIcon className="h-4 w-4" />}
                                              <span className="text-xs font-bold capitalize">{pay.method}</span>
                                            </div>

                                            {/* Reference */}
                                            {pay.reference && (
                                              <span className="text-xs text-gray-400 font-mono truncate flex-1">
                                                {pay.reference}
                                              </span>
                                            )}
                                            {pay.phone && (
                                              <span className="text-xs text-gray-400">{pay.phone}</span>
                                            )}

                                            {/* Date */}
                                            <span className="text-xs text-gray-500 flex-shrink-0 ml-auto">
                                              {fmtDate(pay.date)}
                                            </span>

                                            {/* Amount */}
                                            <span className="font-black text-sm flex-shrink-0" style={{ color: PRIMARY }}>
                                              KES {(pay.amount || 0).toLocaleString()}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}

                                  {/* Overpayment */}
                                  {uid.overpayment > 0 && (
                                    <div className="mt-3 flex items-center gap-2 p-3 rounded-xl bg-green-50 border border-green-200">
                                      <CheckCircleIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
                                      <p className="text-sm text-green-700 font-medium">
                                        KES {uid.overpayment.toLocaleString()} overpayment credited to your wallet
                                      </p>
                                    </div>
                                  )}

                                  {/* Wallet used */}
                                  {uid.walletUsed > 0 && (
                                    <div className="mt-2 flex items-center gap-2 p-3 rounded-xl"
                                      style={{ background: `${PRIMARY}08`, border: `1px solid ${PRIMARY}20` }}>
                                      <WalletIcon className="h-4 w-4 flex-shrink-0" style={{ color: PRIMARY }} />
                                      <p className="text-sm font-medium" style={{ color: PRIMARY }}>
                                        KES {uid.walletUsed.toLocaleString()} used from wallet
                                      </p>
                                    </div>
                                  )}

                                  {/* Summary row */}
                                  <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between text-sm">
                                    <span className="text-gray-500">Total Collected</span>
                                    <span className="font-black" style={{ color: PRIMARY }}>
                                      KES {totalIndexes.toLocaleString()} / KES {(uid.packagePrice || 0).toLocaleString()}
                                    </span>
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ══════════════════════════════════════════════════
                WALLET HISTORY TAB
            ══════════════════════════════════════════════════ */}
            {activeTab === 'wallet' && (
              <div>
                {/* Balance card */}
                <div className="p-5 rounded-2xl mb-5 flex items-center gap-4"
                  style={{ background: `linear-gradient(135deg,${DARK},${PRIMARY})` }}>
                  <WalletIcon className="h-10 w-10 text-white/60 flex-shrink-0" />
                  <div>
                    <p className="text-white/60 text-xs font-medium">Current Wallet Balance</p>
                    <p className="text-white font-black text-2xl">KES {(wallet.balance || 0).toLocaleString()}</p>
                  </div>
                </div>

                {walletTxns.length === 0 ? (
                  <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 shadow-sm">
                    <WalletIcon className="h-12 w-12 text-gray-200 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">No wallet transactions yet</p>
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr style={{ background: `linear-gradient(135deg,${DARK},${PRIMARY})` }}>
                            {['Date', 'Type', 'Amount', 'Balance After', 'Description'].map(h => (
                              <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wide whitespace-nowrap">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {walletTxns.map((txn, i) => (
                            <motion.tr key={txn.id}
                              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                              transition={{ delay: i * 0.02 }}
                              className="hover:bg-gray-50 transition-colors">
                              <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{fmtDateTime(txn.date)}</td>
                              <td className="px-4 py-3">
                                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${txn.type === 'credit' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                                  {txn.type === 'credit' ? '↑ Credit' : '↓ Debit'}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`font-black text-base ${txn.type === 'credit' ? 'text-green-700' : 'text-red-600'}`}>
                                  {txn.type === 'credit' ? '+' : '-'}KES {(txn.amount || 0).toLocaleString()}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className="font-bold text-gray-800">KES {(txn.balance || 0).toLocaleString()}</span>
                              </td>
                              <td className="px-4 py-3 text-xs text-gray-500">{txn.description || txn.source || '—'}</td>
                            </motion.tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Payments;